"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";

interface ThemeSettings {
  textPrimary: string;
  textSecondary: string;
  bgPrimary: string;
  bgSecondary: string;
  fontFamilyPrimary: string;
  fontFamilySecondary: string;
  fontSizeHeading: string;
  fontSizexs: string;
  fontSizesm: string;
  fontSizemd: string;
  fontSizelg: string;
  fontSizeLarge: string;
  btnPrimary: string;
  btnSecondary: string;
  btnText: string;
  btnBgHover: string;
  btnTextHover: string;
  siteWidth: string;

  // Add more properties as needed
}

interface ThemeContextType {
  theme: ThemeSettings;
  updateTheme: (newTheme: Partial<ThemeSettings>) => void;
  loadThemeFromAPI: () => Promise<void>;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const LOCAL_STORAGE_KEY = "app-theme";

const defaultTheme: ThemeSettings = {
  textPrimary: "#111111",
  textSecondary: "#ffffff",
  bgPrimary: "#ffffff",
  bgSecondary: "#F8FAFC",
  fontFamilyPrimary: "'Poppins', sans-serif",
  fontFamilySecondary: "'Lexend', sans-serif",
  fontSizeHeading: "2rem",
  fontSizexs: "0.875rem",
  fontSizesm: "1rem",
  fontSizemd: "1.125rem",
  fontSizelg: "1.25rem",
  fontSizeLarge: "1.5rem",
  btnPrimary: "#4CAF50",
  btnSecondary: "#f44336",
  btnText: "#ffffff",
  btnBgHover: "#45a049",
  btnTextHover: "#ffffff",
  siteWidth: "1200px",
};

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [theme, setTheme] = useState<ThemeSettings>(defaultTheme);

  const applyThemeToDOM = (theme: ThemeSettings) => {
    const root = document.documentElement;

    // Map keys to actual CSS variable names
    const cssVars: Record<string, string> = {
      "--text-primary": theme.textPrimary,
      "--text-secondary": theme.textSecondary,
      "--bg-primary": theme.bgPrimary,
      "--bg-secondary": theme.bgSecondary,
      "--font-primary": theme.fontFamilyPrimary,
      "--font-secondary": theme.fontFamilySecondary,
      "--font-size-xs": theme.fontSizexs,
      " --font-size-sm": theme.fontSizesm,
      "--font-size-md": theme.fontSizemd,
      " --font-size-lg": theme.fontSizelg,
      "--button-primary-bg": theme.btnPrimary,
      "--button-secondary-bg": theme.btnSecondary,
      "--button-primary-text": theme.btnText,
      "--button-hover-bg": theme.btnBgHover,
      "--button-hover-text": theme.btnTextHover,
      "--site-container": theme.siteWidth,
    };

    Object.entries(cssVars).forEach(([cssVar, value]) => {
      root.style.setProperty(cssVar, value);
    });
  };

  const updateTheme = (newTheme: Partial<ThemeSettings>) => {
    if (!theme) return;
    const merged = { ...theme, ...newTheme };
    setTheme(merged);
    applyThemeToDOM(merged);
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(merged));
  };

  const loadThemeFromAPI = async () => {
    try {
      const simulatedResponse = await new Promise((resolve) =>
        setTimeout(() => {
          resolve({
            textPrimary: "#111111", // red
            textSecondary: "#ffffff", // white
            bgPrimary: "#ffffff",
            bgSecondary: "#F8FAFC", // black
            fontFamilyPrimary: "'Poppins', sans-serif",
            fontFamilySecondary: "'Lexend', sans-serif",
            fontSizexs: "0.875rem",
            fontSizesm: "1rem",
            fontSizemd: "1.125rem",
            fontSizelg: "1.25rem",
            btnPrimary: "#4CAF50", // green
            btnSecondary: "#f44336", // red
            btnText: "#ffffff",
            btnBgHover: "#45a049", // darker green
            btnTextHover: "#ffffff",
            siteWidth: "1200px",
          });
        }, 500)
      );

      const newTheme = simulatedResponse as ThemeSettings;
      setTheme(newTheme);
      applyThemeToDOM(newTheme);
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newTheme));
    } catch (err) {
      console.error("Failed to load theme from API", err);
    }
  };

  useEffect(() => {
    loadThemeFromAPI();
  }, [loadThemeFromAPI]);

  useEffect(() => {
    if (theme) applyThemeToDOM(theme);
  }, [theme]);



  return (
    <ThemeContext.Provider value={{ theme, updateTheme, loadThemeFromAPI }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used within ThemeProvider");
  return context;
};
